<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useApplicationsStore } from '@/stores/applications'
import { triggerSidebarHighlight } from '@/utils/sidebarBus'

const router = useRouter()
const applicationsStore = useApplicationsStore()
const activeTabIndex = ref(0)
const diagramExpanded = ref(false)

// Load applications on mount
onMounted(async () => {
  await applicationsStore.fetchApplications()
  // 首页加载时获取所有推荐套餐
  applicationsStore.fetchBundleProducts()
  // 加载默认应用领域的产品
  if (applicationsStore.applications.length > 0) {
    await loadApplicationProducts(0)
  }
})

const activeApplication = computed(() =>
  applicationsStore.applications[activeTabIndex.value]
)

// 获取应用领域的相关产品
const loadApplicationProducts = async (index) => {
  const app = applicationsStore.applications[index]
  if (app && app.id) {
    await applicationsStore.fetchProductsByApplicationField(app.id)
  }
}

const switchTab = async (index) => {
  activeTabIndex.value = index
  diagramExpanded.value = false
  // 点击标签时加载该应用领域的产品
  await loadApplicationProducts(index)
}

// const goToProductDetail = (productId) => {
//   router.push(`/products/${productId}`)
// }

// 下载数据手册
const downloadDatasheet = (product) => {
  if (product.datasheet_url && product.datasheet_url !== 'ces') {
    window.open(product.datasheet_url, '_blank')
  } else {
    console.warn('暂无有效的数据手册URL')
  }
}

// 佣金计算
const goToCommissionCalc = (product) => {
  router.push({ path: '/commission/calc', query: { productId: product.id } })
}

// 订购套餐 → 改为查看套餐详情
const handleBundleOrder = () => {
  // TODO: navigate to bundle detail or show info
}

// 申请样品（从产品列表）
const goToOrder = (product) => {
  router.push({ path: '/sample/apply', query: { productId: product.id } })
}

// 加入购物车
// const addToCart = (product) => {
//   console.log('加入购物车:', {
//     id: product.id,
//     product_name: product.product_name,
//     model: product.model,
//     price: product.price?.current_price,
//     quantity: 1
//   })
// }
</script>

<template>
  <div class="applications-page">
    <!-- 数据来源提示 -->
    <div v-if="applicationsStore.usingMockData" class="data-source-notice">
      <p>ℹ️ 当前使用模拟数据（API 不可用）</p>
    </div>

    <!-- Hero Banner -->
    <section class="apps-hero">
      <div class="hero-overlay"></div>
      <div class="container hero-content">
        <h1>按应用找产品 · 快速开发客户</h1>
        <p>按场景查看适配芯片，申请样品给客户测试，并预估回款后的佣金提成</p>
      </div>
    </section>

    <!-- 加载中状态 -->
    <div v-if="applicationsStore.loading" class="container loading-state">
      <div class="spinner"></div>
      <p>加载中...</p>
    </div>

    <div v-else class="container">
      <!-- 销售合伙人奖励横幅 -->
      <div class="partner-banner">
        <div class="partner-banner-inner">
          <span class="partner-icon">💰</span>
          <div class="partner-text">
            <span class="partner-title">销售合伙人计划</span>
            <span class="partner-divider">·</span>
            <span class="partner-desc">常规成交佣金中位数约 <strong>10%</strong>，样品测试与客户开发工具一站配齐，回款后按规则结算佣金提成</span>
          </div>
          <!-- <a href="/about-us" class="partner-cta">了解详情 →</a> -->
          <button class="partner-cta" @click="triggerSidebarHighlight">联系咨询 →</button>
        </div>
      </div>

      <!-- Tabs -->
      <div class="tabs-section">
        <div class="tabs-container">
          <button
            v-for="(app, index) in applicationsStore.applications"
            :key="index"
            :class="['tab', { active: activeTabIndex === index }]"
            @click="switchTab(index)"
          >
            {{ app.name }}
          </button>
        </div>
      </div>

      <!-- Application Detail Section -->
      <div v-if="activeApplication" class="app-detail-section">

        <!-- 应用框图：折叠条，默认收起 -->
        <div class="diagram-collapse" :class="{ expanded: diagramExpanded }">
          <button class="diagram-toggle" @click="diagramExpanded = !diagramExpanded">
            <span class="toggle-label">📐 应用框图 · {{ activeApplication.name }}</span>
            <span class="toggle-icon">{{ diagramExpanded ? '▲' : '▼' }}</span>
          </button>
          <div class="diagram-body" v-show="diagramExpanded">
            <img
              :src="activeApplication.diagram_url"
              :alt="activeApplication.name"
              loading="lazy"
              class="diagram-img"
            />
          </div>
        </div>

        <!-- 产品列表（全宽主区域） -->
        <div v-if="applicationsStore.loading || applicationsStore.applicationProducts.length > 0" class="products-section">
          <h3 class="products-title">相关产品</h3>

          <!-- 产品加载中状态 -->
          <div v-if="applicationsStore.loading" class="loading-state">
            <div class="spinner"></div>
            <p>加载产品中...</p>
          </div>

          <!-- 产品列表 -->
          <div v-else class="products-grid">
            <div v-for="product in applicationsStore.applicationProducts" :key="product.id" class="product-card">
              <!-- Product Image -->
              <div class="product-image">
                <img :src="product.cover_url" :alt="product.product_name" />
              </div>

              <!-- Product Info -->
              <div class="product-info">
                <!-- Title and Model in one line -->
                <div class="product-header">
                  <h4 class="product-title">{{ product.product_name }}</h4>
                  <!-- <p class="product-model">{{ product.model }}</p> -->
                </div>

                <!-- Package and Stock in one line -->
                <div class="info-row">
                  <div v-if="product.package" class="info-cell">
                    <span class="info-label">封装:</span>
                    <span class="info-value">{{ product.package }}</span>
                  </div>
                  <!-- <div class="info-cell">
                    <span class="info-label">库存:</span>
                    <span class="info-value">{{ product.stock_qty > 10000 ? (product.stock_qty / 10000).toFixed(1) + '万' : product.stock_qty }}</span>
                  </div> -->
                  <div v-if="product.datasheet_url && product.datasheet_url !== 'ces'" class="info-cell">
                    <span class="info-label">产品手册:</span>
                    <button class="datasheet-link" @click="downloadDatasheet(product)" :title="product.datasheet_name">
                      下载
                    </button>
                  </div>
                </div>

                <!-- Features - 突出显示 -->
                <div v-if="product.features" class="features-section">
                  <div class="features-label">产品特点</div>
                  <div class="features-content">{{ product.features }}</div>
                </div>

                <!-- Action Buttons - 根据产品模式显示不同的UI -->
                <div class="product-actions-container">
                  <div class="primary-actions">
                    <button class="btn-primary btn-buy btn-compact" title="申请样品" @click="goToOrder(product)">
                      <span class="btn-icon">📦</span>
                      <span class="btn-text">申请样品</span>
                    </button>
                    <button class="btn-primary btn-calc btn-compact" title="佣金计算" @click="goToCommissionCalc(product)">
                      <span class="btn-icon">💰</span>
                      <span class="btn-text">佣金计算</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Bundle Products List -->
        <div v-if="applicationsStore.bundleProducts.length > 0" class="bundles-section">
          <div class="section-header">
            <h3 class="products-title">推荐套餐</h3>
            <span class="bundle-badge">一体化方案 · 采购更快捷</span>
          </div>
          <div class="bundles-grid">
            <div v-for="bundle in applicationsStore.bundleProducts" :key="bundle.id" class="bundle-card-composite">
              <div class="bundle-main-info">
                <!-- Cover: First Product's Image -->
                <div class="bundle-cover">
                  <img :src="bundle.items?.[0]?.product?.cover_url" :alt="bundle.bundle_name" />
                  <span class="bundle-tag">系统套餐</span>
                </div>
                
                <div class="bundle-header">
                  <h4 class="bundle-name text-gradient">{{ bundle.bundle_name }}</h4>
                  <p class="bundle-description">{{ bundle.description || '官方组建的完整应用系统方案，一站式解决采购需求。' }}</p>
                </div>
              </div>

              <!-- Detailed Items List -->
              <div class="bundle-items-details">
                <div class="items-list-title">包含单品（{{ bundle.items?.length || 0 }}）</div>
                <div class="items-scroll-area">
                  <div v-for="item in bundle.items" :key="item.id" class="mini-product-row">
                    <div class="mini-img">
                      <img :src="item.product.cover_url" :alt="item.product.product_name" />
                    </div>
                    <div class="mini-info">
                      <div class="mini-name">{{ item.product.product_name }}</div>
                      <div class="mini-meta">
                        <span>{{ item.product.model }}</span>
                        <span class="divider">|</span>
                        <span>{{ item.product.package }}</span>
                      </div>
                    </div>
                    <div class="mini-qty">
                      <span class="qty-val">{{ item.reel_qty }}</span> <span class="qty-unit">盘</span>
                      <span v-if="item.product?.reel_units" class="qty-sub">({{ item.product.reel_units }}颗/盘)</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Bundle Bottom Action Area -->
              <div class="bundle-footer">
                <button class="btn-buy-bundle" @click="handleBundleOrder(bundle)">
                  <span class="icon">📋</span>
                  <span class="text">查看方案详情</span>
                </button>
              </div>
            </div><!-- end bundle-card-composite -->
          </div><!-- end bundles-grid -->
        </div><!-- end bundles-section -->

      </div><!-- end app-detail-section -->
    </div><!-- end container -->
  </div><!-- end applications-page -->
</template>

<style scoped>
.data-source-notice {
  background: #fff3cd;
  border-bottom: 2px solid #ffc107;
  padding: 12px 0;
  text-align: center;
  margin-bottom: 0;
  position: relative;
  z-index: 10;
}

.data-source-notice p {
  margin: 0;
  color: #856404;
  font-size: 13px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 500px;
  text-align: center;
}

.loading-state .spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #005eb8;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.applications-page {
  background-color: #f9f9f9;
  min-height: 100vh;
  padding-bottom: 60px;
}

/* Hero Section */
.apps-hero {
  position: relative;
  height: 300px;
  background: url('@/assets/images/apps-banner.png') center/cover;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #ffffff;
  margin-bottom: 40px;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(0, 94, 184, 0.8), rgba(0, 0, 0, 0.6));
}

.hero-content {
  position: relative;
  z-index: 1;
}

.hero-content h1 {
  font-size: 40px;
  font-weight: 800;
  margin-bottom: 12px;
}

.hero-content p {
  font-size: 18px;
  opacity: 0.9;
}

/* Page Header */
.page-header {
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e0e0e0;
}

.page-title {
  font-size: 36px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
}

.page-subtitle {
  font-size: 16px;
  color: #666;
  margin: 0;
}

/* Partner Banner */
.partner-banner {
  margin: 20px 0 20px;
  background: linear-gradient(135deg, #fffbe6 0%, #fff7d6 100%);
  border: 1px solid #ffe58f;
  border-radius: 10px;
  padding: 14px 20px;
}

.partner-banner-inner {
  display: flex;
  align-items: center;
  gap: 12px;
}

.partner-icon {
  font-size: 22px;
  flex-shrink: 0;
}

.partner-text {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 14px;
  color: #7c5800;
}

.partner-title {
  font-weight: 800;
  font-size: 15px;
  color: #d46b08;
  white-space: nowrap;
}

.partner-divider {
  color: #ffc53d;
  font-weight: 700;
}

.partner-desc strong {
  color: #d46b08;
  font-size: 16px;
}

.partner-cta {
  flex-shrink: 0;
  background: linear-gradient(135deg, #fa8c16, #d46b08);
  color: white;
  text-decoration: none;
  padding: 7px 18px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(250, 140, 22, 0.35);
  border: none;
  cursor: pointer;
}

.partner-cta:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(250, 140, 22, 0.45);
}

/* Tabs Section */
.tabs-section {
  margin-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
}

.tabs-container {
  display: flex;
  gap: 20px;
  padding-bottom: 20px;
}

.tab {
  background: none;
  border: none;
  padding: 0;
  font-size: 16px;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  position: relative;
  transition: color 0.3s ease;
}

.tab:hover {
  color: #005eb8;
}

.tab.active {
  color: #005eb8;
}

.tab.active::after {
  content: '';
  position: absolute;
  bottom: -20px;
  left: 0;
  right: 0;
  height: 3px;
  background-color: #005eb8;
}

/* Application Detail Section */
.app-detail-section {
  margin-bottom: 25px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 框图折叠条 */
.diagram-collapse {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  background: white;
}

.diagram-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8fbff;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #005eb8;
  transition: background 0.2s;
}

.diagram-toggle:hover {
  background: #eef4fb;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 6px;
}

.toggle-icon {
  font-size: 11px;
  color: #999;
}

.diagram-body {
  padding: 12px;
  border-top: 1px solid #f0f0f0;
}

.diagram-img {
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
}

/* Products Section */
.products-section {
  background: white;
  border-radius: 8px;
  padding: 35px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.bundles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;
}

.bundle-card-composite {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  height: 100%;
}

.bundle-card-composite:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 30px rgba(0, 136, 255, 0.12);
  border-color: #0088ff;
}

.bundle-main-info {
  display: flex;
  gap: 20px;
  padding: 24px;
  background: linear-gradient(135deg, #f8faff 0%, #ffffff 100%);
  border-bottom: 1px dashed #e2e8f0;
}

.bundle-cover {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f1f5f9;
  border: 1px solid #f1f5f9;
}

.bundle-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.bundle-tag {
  position: absolute;
  top: 0;
  left: 0;
  background: #ff9800;
  color: white;
  font-size: 10px;
  padding: 2px 8px;
  font-weight: 700;
  border-bottom-right-radius: 8px;
}

.bundle-header {
  flex: 1;
}

.bundle-name {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 8px;
}

.bundle-description {
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-break: anywhere;
  overflow: hidden;
}

.bundle-items-details {
  padding: 20px 24px;
  flex: 1;
}

.items-list-title {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.items-list-title::before {
  content: '';
  width: 3px;
  height: 14px;
  background: #0088ff;
  border-radius: 4px;
}

.items-scroll-area {
  max-height: 200px;
  overflow-y: auto;
  padding-right: 8px;
}

/* Custom Scrollbar */
.items-scroll-area::-webkit-scrollbar {
  width: 4px;
}
.items-scroll-area::-webkit-scrollbar-track {
  background: #f1f5f9;
}
.items-scroll-area::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}

.mini-product-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
}

.mini-product-row:last-child {
  border-bottom: none;
}

.mini-img {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  overflow: hidden;
  background: #f8fafc;
}

.mini-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mini-info {
  flex: 1;
}

.mini-name {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 2px;
}

.mini-meta {
  font-size: 11px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.mini-prices {
  display: flex;
  gap: 12px;
  font-size: 11px;
  color: #64748b;
  align-items: baseline;
}

.mini-price-item {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

/* 颗价突出显示 */
.mini-price-item:first-child {
  color: #d84315;
  font-weight: 700;
}

.mini-price-item:first-child .price-val {
  font-size: 16px;
  font-weight: 800;
  color: #d84315;
  font-style: normal;
}

.mini-price-item .price-val {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  font-style: normal;
}

.mini-qty {
  text-align: right;
  white-space: nowrap;
}

.qty-val {
  font-weight: 700;
  color: #0088ff;
  font-size: 14px;
}

.qty-unit {
  font-size: 11px;
  color: #94a3b8;
}

.qty-sub {
  display: block;
  font-size: 10px;
  color: #94a3b8;
  margin-top: 2px;
}

.bundle-footer {
  padding: 24px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
}

.bundle-price-area {
  display: flex;
  flex-direction: column;
}

.price-label {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 2px;
}

.price-val {
  font-size: 22px;
  font-weight: 800;
  color: #0088ff;
}

.btn-buy-bundle {
  background: linear-gradient(135deg, #0088ff 0%, #005eb8 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(0, 136, 255, 0.3);
}

.btn-buy-bundle:hover {
  transform: scale(1.02);
  box-shadow: 0 6px 16px rgba(0, 136, 255, 0.4);
}

.text-gradient {
  background: linear-gradient(135deg, #1e293b 0%, #0088ff 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.products-title {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 28px 0;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f0f0;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

/* Product Card */
.product-card {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 12px;
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  transition: all 0.2s ease;
  align-items: flex-start;
}

.product-card:hover {
  border-color: #e0e0e0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

/* Product Image */
.product-image {
  position: relative;
  width: 140px;
  height: 105px;
  border-radius: 6px;
  overflow: hidden;
  background: #f9f9f9;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-tag {
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(0, 94, 184, 0.9);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

/* Product Info */
.product-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: space-between;
  height: 100%;
}

.product-header {
  flex-shrink: 0;
}

.product-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-model {
  font-size: 11px;
  color: #999;
  margin: 0;
  line-height: 1.2;
}

/* Info Row - 一行显示多个信息 */
.info-row {
  display: flex;
  gap: 12px;
  padding: 0;
  margin: 0;
  flex-wrap: wrap;
  flex-shrink: 0;
  align-items: center;
}

.info-cell {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  white-space: nowrap;
}

.info-label {
  color: #999;
  font-weight: 600;
  flex-shrink: 0;
  font-size: 11px;
}

.info-value {
  color: #333;
  font-weight: 500;
  font-size: 11px;
}

/* Price Row - 颗价和盘价在一行 */
.price-row {
  display: flex;
  gap: 12px;
  padding: 0;
  margin: 4px 0;
  flex-wrap: wrap;
  flex-shrink: 0;
  align-items: baseline;
}

.price-cell {
  display: flex;
  align-items: baseline;
  gap: 4px;
  white-space: nowrap;
}

/* 颗价突出显示 */
.price-cell:first-child .price-label {
  color: #d84315;
  font-weight: 700;
  font-size: 11px;
}

.price-cell:first-child .price-value {
  color: #d84315;
  font-weight: 800;
  font-size: 20px;
}

.price-label {
  color: #999;
  font-weight: 600;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.price-value {
  color: #666;
  font-weight: 600;
  font-size: 12px;
}

.reel-units-text {
  font-size: 10px;
  color: #999;
  font-weight: 500;
}

.saving-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: linear-gradient(135deg, #ff4d00, #ff8c00);
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 6px rgba(255, 77, 0, 0.35);
}

.saving-arrow {
  font-size: 13px;
  font-weight: 900;
  line-height: 1;
}

/* Datasheet Download Link */
.datasheet-link {
  background: none;
  border: none;
  color: #005eb8;
  font-weight: 600;
  font-size: 11px;
  cursor: pointer;
  padding: 0;
  text-decoration: none;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.datasheet-link:hover {
  color: #004a94;
  text-decoration: underline;
}

/* 响应式 */
@media (max-width: 1200px) {
  .products-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .product-card {
    grid-template-columns: 130px 1fr;
    gap: 10px;
    padding: 10px;
  }

  .product-image {
    width: 130px;
    height: 98px;
  }

  .product-title {
    font-size: 13px;
  }
}

@media (max-width: 768px) {
  .products-grid {
    grid-template-columns: 1fr;
  }

  .product-card {
    grid-template-columns: 1fr;
    gap: 10px;
    padding: 10px;
  }

  .product-image {
    width: 100%;
    height: 120px;
  }

  .product-title {
    font-size: 13px;
  }

  .btn-primary.btn-compact {
    padding: 5px 8px;
    font-size: 10px;
  }
}

/* Features Section - 突出显示特点 */
.features-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px;
  background: #f0f8ff;
  border-radius: 4px;
  border-left: 3px solid #005eb8;
  overflow: hidden;
}

.features-label {
  font-size: 11px;
  font-weight: 700;
  color: #005eb8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.features-content {
  font-size: 11px;
  color: #333;
  line-height: 1.3;
  overflow-y: auto;
  max-height: 50px;
  word-break: break-word;
}

/* Product Actions Container - 操作按钮容器 */
.product-actions-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 6px;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;
}

/* Primary Actions - 主要操作按钮 */
.primary-actions {
  display: flex;
  gap: 6px;
  align-items: center;
}

/* Order Section - 订单部分（期货/期货+现货模式） */
.order-section {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
}

.quantity-input-group {
  display: flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
}

.quantity-label {
  font-size: 11px;
  font-weight: 600;
  color: #666;
  white-space: nowrap;
}

.quantity-input {
  width: 70px;
  padding: 3px 5px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 11px;
  background: white;
  transition: border-color 0.2s ease;
  height: 26px;
}

.quantity-input:focus {
  outline: none;
  border-color: #005eb8;
  box-shadow: 0 0 0 2px rgba(0, 94, 184, 0.1);
}

.quantity-input::placeholder {
  color: #999;
}

/* Primary Button - 突出显示的按钮 */
.btn-primary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 6px 10px;
  border: none;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
  flex-wrap: wrap;
}

/* 紧凑按钮 - 用于现货模式和期货模式 */
.btn-primary.btn-compact {
  padding: 5px 8px;
  font-size: 11px;
  gap: 2px;
  flex-shrink: 0;
  white-space: nowrap;
  min-width: fit-content;
  width: auto;
}

.btn-primary.btn-download {
  background: linear-gradient(135deg, #ffc107 0%, #ff9800 100%);
}

.btn-primary.btn-download:hover {
  background: linear-gradient(135deg, #ffb300 0%, #f58f0a 100%);
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(255, 152, 0, 0.2);
}

.btn-primary.btn-buy {
  background: linear-gradient(135deg, #0088ff 0%, #005eb8 100%);
}

.btn-primary.btn-buy:hover {
  background: linear-gradient(135deg, #006fd6 0%, #004a94 100%);
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 94, 184, 0.25);
}

.btn-primary.btn-calc {
  background: linear-gradient(135deg, #0088ff 0%, #005eb8 100%);
}

.btn-primary.btn-calc:hover {
  background: linear-gradient(135deg, #006fd6 0%, #004a94 100%);
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 94, 184, 0.25);
}

.btn-icon {
  font-size: 16px;
  display: flex;
  align-items: center;
}

.btn-text {
  white-space: nowrap;
}

/* Secondary Actions - 辅助操作按钮 */
.secondary-actions {
  display: flex;
  gap: 8px;
}

.secondary-actions .btn-mini {
  flex: 1;
  padding: 8px 6px;
}

/* Updated Mini Button Styles */
.btn-mini {
  padding: 6px 4px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  color: #333;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-mini:hover {
  border-color: #005eb8;
  background: #f0f8ff;
}

.btn-download:hover {
  border-color: #ffc107;
  background: #fffbf0;
}

.btn-cart:hover {
  border-color: #4caf50;
  background: #f1f8f4;
}

.btn-buy:hover {
  border-color: #005eb8;
  background: #f0f8ff;
}

.btn-details:hover {
  border-color: #005eb8;
  background: #f0f8ff;
}

.stock-status.in-stock {
  background: #e8f5e9;
  color: #2e7d32;
}

.stock-status.low-stock {
  background: #fff3e0;
  color: #e65100;
}

.stock-status.out-of-stock {
  background: #ffebee;
  color: #c62828;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
  color: #999;
}

.empty-state p {
  font-size: 16px;
  margin: 0;
  color: #999;
}

/* Products Section Loading State */
.products-section .loading-state {
  min-height: 200px;
  padding: 20px;
}

.products-section .loading-state .spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #005eb8;
  margin-bottom: 12px;
}

.products-section .loading-state p {
  font-size: 14px;
  color: #666;
  margin: 0;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .product-card {
    grid-template-columns: 240px 1fr;
  }

  .product-image {
    width: 240px;
    height: 180px;
  }

  .specs-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .page-title {
    font-size: 24px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .product-card {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .product-image {
    width: 100%;
    height: 200px;
  }

  .specs-grid {
    grid-template-columns: 1fr;
  }
}
</style>
